import React from 'react'

/**
 * SCENARIO LOADING SKELETON
 *
 * Displays a shimmer-animated skeleton placeholder that mimics the scenario detail page layout.
 * Used as the loading state while fetchTestScenarioData is in progress.
 *
 * Structure mirrors the real page:
 * - Header section (breadcrumb bar)
 * - Scenario metadata (title, description, avatar, priority)
 * - Table rows (test cases and steps with execution columns)
 *
 * Uses CSS-based shimmer animation for Jira-native feel.
 * Reuses existing layout classes (test-management-container, header-section, etc.)
 * for structural consistency with the loaded page.
 */
export function ScenarioLoadingSkeleton() {
  return (
    <div className="test-management-container">
      {/* Skeleton Header Section - mimics HeaderSection breadcrumb bar */}
      <div className="header-section">
        <div className="skeleton-breadcrumb">
          {/* Breadcrumb segments: Project > Release > Scenario */}
          <div className="skeleton-bone" style={{ width: '80px', height: '14px' }} />
          <span className="skeleton-breadcrumb-separator">/</span>
          <div className="skeleton-bone" style={{ width: '100px', height: '14px' }} />
          <span className="skeleton-breadcrumb-separator">/</span>
          <div className="skeleton-bone" style={{ width: '60px', height: '14px' }} />
        </div>
        {/* Right side - progress bar and buttons placeholder */}
        <div className="skeleton-header-right">
          <div className="skeleton-bone" style={{ width: '200px', height: '8px', borderRadius: '4px' }} />
          <div className="skeleton-bone" style={{ width: '40px', height: '14px' }} />
          <div className="skeleton-bone" style={{ width: '70px', height: '28px', borderRadius: '3px' }} />
          <div className="skeleton-bone" style={{ width: '80px', height: '28px', borderRadius: '3px' }} />
        </div>
      </div>

      {/* Skeleton Content Area - mimics scenario-content-wrapper + card */}
      <div className="scenario-content-wrapper">
        <div className="scenario-content-card">
          {/* Scenario header - title, description, metadata */}
          <div className="skeleton-scenario-header">
            {/* Title skeleton */}
            <div className="skeleton-bone" style={{ width: '45%', height: '22px', marginBottom: '8px' }} />
            {/* Description skeleton */}
            <div className="skeleton-bone" style={{ width: '65%', height: '14px', marginBottom: '12px' }} />
            {/* Metadata bar skeleton: labeled field groups separated by dividers */}
            <div className="skeleton-metadata-row" style={{ gap: '0' }}>
              {/* Assignee field: icon + label + avatar + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px 4px 0' }}>
                <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px' }} />
                <div className="skeleton-bone" style={{ width: '52px', height: '10px', borderRadius: '2px' }} />
                <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px' }} />
                <div className="skeleton-bone" style={{ width: '64px', height: '10px', borderRadius: '2px' }} />
              </div>
              {/* Divider */}
              <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--ds-border, #DFE1E6)', flexShrink: 0, margin: '0 4px' }} />
              {/* Priority field: icon + label + lozenge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px 4px 8px' }}>
                <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px' }} />
                <div className="skeleton-bone" style={{ width: '44px', height: '10px', borderRadius: '2px' }} />
                <div className="skeleton-bone" style={{ width: '52px', height: '18px', borderRadius: '3px' }} />
              </div>
              {/* Divider */}
              <div style={{ width: '1px', height: '16px', backgroundColor: 'var(--ds-border, #DFE1E6)', flexShrink: 0, margin: '0 4px' }} />
              {/* Labels field: icon + label + tag */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0 4px 8px' }}>
                <div className="skeleton-bone skeleton-circle" style={{ width: '16px', height: '16px' }} />
                <div className="skeleton-bone" style={{ width: '36px', height: '10px', borderRadius: '2px' }} />
                <div className="skeleton-bone" style={{ width: '50px', height: '20px', borderRadius: '10px' }} />
              </div>
            </div>
          </div>

          {/* Skeleton table rows - alternating test case and step patterns */}
          <div className="skeleton-table">
            {/* Test Case 1 */}
            <div className="skeleton-row skeleton-row-case">
              <div className="skeleton-row-left">
                {/* Expand icon placeholder */}
                <div className="skeleton-bone" style={{ width: '20px', height: '20px', borderRadius: '3px' }} />
                {/* Test number badge */}
                <div className="skeleton-bone" style={{ width: '32px', height: '18px', borderRadius: '3px' }} />
                {/* Test case title */}
                <div className="skeleton-bone" style={{ width: '55%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                {/* Execution column placeholders */}
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Step 1.1 */}
            <div className="skeleton-row skeleton-row-step">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '28px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '40%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Step 1.2 */}
            <div className="skeleton-row skeleton-row-step">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '28px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '50%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Test Case 2 */}
            <div className="skeleton-row skeleton-row-case">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '20px', height: '20px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '32px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '45%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Step 2.1 */}
            <div className="skeleton-row skeleton-row-step">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '28px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '35%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Test Case 3 */}
            <div className="skeleton-row skeleton-row-case">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '20px', height: '20px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '32px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '60%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>

            {/* Step 3.1 */}
            <div className="skeleton-row skeleton-row-step">
              <div className="skeleton-row-left">
                <div className="skeleton-bone" style={{ width: '28px', height: '18px', borderRadius: '3px' }} />
                <div className="skeleton-bone" style={{ width: '48%', height: '14px' }} />
              </div>
              <div className="skeleton-row-right">
                <div className="skeleton-bone skeleton-execution" />
                <div className="skeleton-bone skeleton-execution" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
